import React from 'react';
import { ICellRendererParams } from '@ag-grid-community/core';
import { Flex } from '@amzn/storm-ui';

import { CustomIconButton } from '../../../UiComponentsLibrary/CustomIconButton';
import { useAppContext } from '../../store/appContext';
import { ART_EVENT_STATUS } from '../../../EditArtEvent/constants/artEventStatus';
import { BOOKING_ACTION_IDS } from '../../../BookingManagement/constants/actionIds';
import { canUserEdit } from '../../utils/helpers';

interface ActionIconConfig {
  id: string;
  icon: React.ReactNode;
  label: string;
  backgroundColor: string;
  handleOnClickCb: (data: unknown) => void;
}

interface UDCActionCellRendererProps {
  actionButtonConfig: ActionIconConfig[];
}

export const UDCActionCellRenderer =
  ({ actionButtonConfig }: UDCActionCellRendererProps) =>
  (params: ICellRendererParams) => {
    const { state: { selectedArtEventStatus, currentUser } } = useAppContext();
    const isFrozen = selectedArtEventStatus === ART_EVENT_STATUS.FROZEN;
    const canEdit = canUserEdit(currentUser?.permissions);
    const isPrivilegedMode = currentUser?.privilegedMode ?? false;

    const isButtonDisabled = (actionId: string) => {
      if (actionId === BOOKING_ACTION_IDS.VIEW) return false;
      if (!canEdit) return true;
      if (isFrozen && !isPrivilegedMode) return true;
      return false;
    };

    const handleActionClick = (
      event: React.MouseEvent,
      actionCallback: (data: unknown) => void
    ) => {
      event?.preventDefault();
      event?.stopPropagation();
      actionCallback(params.data);
    };

    return (
      <Flex direction="row" columnGap="xsmall">
        {actionButtonConfig.map((action) => (
          <CustomIconButton
            key={`${action.id}-${params.data?.entryId || params.rowIndex}`}
            icon={action.icon}
            label={action.label}
            backgroundColor={action.backgroundColor}
            disabled={isButtonDisabled(action.id)}
            handleOnClickCb={(event) =>
              handleActionClick(event, action.handleOnClickCb)
            }
          />
        ))}
      </Flex>
    );
  };
